import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// Renders a static legal document from /public/legal/<file>. The owner edits the
// file directly (HTML) and the page reflects it — no React changes needed.
export default function LegalPage({ file, title, testid = "legal-page" }) {
  const [html, setHtml] = useState("");
  const [status, setStatus] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let mounted = true;
    fetch(`/legal/${file}`)
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.text();
      })
      .then((t) => {
        if (!mounted) return;
        setHtml(t);
        setStatus("ready");
      })
      .catch(() => mounted && setStatus("error"));
    return () => {
      mounted = false;
    };
  }, [file]);

  return (
    <main
      data-testid={testid}
      className="relative min-h-screen bg-[#FAFAFA] text-[#0A0A0A]"
    >
      <header className="border-b border-black/10 bg-white/70 backdrop-blur sticky top-0 z-30">
        <div className="mx-auto max-w-3xl px-6 md:px-8 h-14 flex items-center justify-between">
          <Link to="/" data-testid={`${testid}-brand`} className="flex items-center gap-2">
            <span className="h-5 w-5 rounded-full bg-[#0A0A0A] grid place-items-center">
              <span className="h-1 w-1 rounded-full bg-[#FAFAFA]" />
            </span>
            <span className="text-[15px] font-medium tracking-tight">Fittin</span>
          </Link>
          <Link
            to="/"
            data-testid={`${testid}-back`}
            className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-[12px] hover:bg-neutral-50 transition-all"
          >
            <ArrowLeft className="h-3 w-3" />
            Back
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-6 md:px-8 py-12 md:py-16">
        {status === "loading" && (
          <div data-testid={`${testid}-loading`} className="text-[14px] text-neutral-500">
            Loading {title}…
          </div>
        )}

        {status === "error" && (
          <div data-testid={`${testid}-error`}>
            <h1 className="text-3xl tracking-tight font-medium">{title}</h1>
            <p className="mt-3 text-[14px] text-neutral-600">
              This document is being prepared and will be available shortly.
            </p>
          </div>
        )}

        {status === "ready" && (
          <article
            data-testid={`${testid}-content`}
            className="text-[15px] leading-relaxed text-neutral-700 [&_h1]:text-3xl [&_h1]:sm:text-4xl [&_h1]:tracking-[-0.02em] [&_h1]:font-medium [&_h1]:text-[#0A0A0A] [&_h1]:mb-6 [&_h2]:text-lg [&_h2]:font-medium [&_h2]:text-[#0A0A0A] [&_h2]:mt-8 [&_h2]:mb-2 [&_h3]:font-medium [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4 [&_li]:mb-1 [&_a]:underline [&_a]:underline-offset-2 [&_small]:text-neutral-400"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
      </div>
    </main>
  );
}
